import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMobileOptimizedStyles } from '../../hooks/mobile-hooks';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Popover,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
  Menu as MenuIcon,
  Toll as TokenIcon,
  ViewModule as ViewModuleIcon,
} from '@mui/icons-material';
import type {
  PublicBestByUnitTypePayload,
  PublicBestByUnitTypeRow,
  PublicTowerViewUnit,
} from '../../services/superPickerPublicApi';
import { useRevealedSection, useRevealedPrices } from '../../services/revealedPrices';
import { getPublicShareUuid, getShareTokens, subscribeShareTokens } from '../../services/shareContext';
import { revealSection, type RevealedTowerViewUnitPrices } from '../../services/superPickerPublicApi';
import { useTokenSpend } from '../TokenSpendDialog';
import { buildPublicUnitSyntheticKey } from './unit/types';
import { isUnitStatusNotReleased } from '../../../utils/formatting';
import type { SitePlanBlock, SitePlanBounds, StackUnitInfo } from '../../types';
import type { PublicUnitForMenu } from './unit/types';
import ColumnVisibilityMenu, {
  ColumnVisibilityState,
} from './unit/ColumnVisibilityMenu';
import TowerViewSection from './TowerViewSection';
import SitePlanSectionDetail from './SitePlanSectionDetail';
import { isGroundFloorNumber, parseUnitNumber } from '../../utils/parseUnitNumber';

interface AvailableUnitsSectionProps {
  /**
   * Project id this section is showing. Required for the per-section
   * "Reveal prices" flow, which calls `POST .../share/:uuid/reveal` with
   * `{ section: 'towerView', projectId }`. Without it the reveal CTA is
   * hidden (the section degrades to masked-only).
   */
  projectId?: string | null;
  /** Live, masked-pricing tower-view rows from the public API. */
  liveAvailableUnits?: PublicTowerViewUnit[] | null;
  /**
   * Called when the user clicks a unit row in any of the three tabs. The
   * parent (`ProjectDetailPage`) opens the unit-actions menu at the
   * given viewport coordinates. Mirrors the agent's `setSectionUnitMenuAnchor`
   * pattern from `TopViewingSitePlanDetail.tsx`.
   */
  onUnitClick?: (unit: PublicUnitForMenu, mouseX: number, mouseY: number) => void;
  /** Live "Best PX bedroom type" payload (sorted by selling price). */
  bestByPrice?: PublicBestByUnitTypePayload | null;
  /** Live "Best PSF bedroom type" payload (sorted by PSF). */
  bestByPsf?: PublicBestByUnitTypePayload | null;
  /** Optional site-plan image URL rendered in the Best PX/PSF side panel. */
  sitePlanImageUrl?: string | null;
  sitePlanBlocks?: SitePlanBlock[];
  sitePlanBounds?: SitePlanBounds | null;
  stackInfos?: StackUnitInfo[];
  stackSoldPercentages?: Record<string, number> | null;
  projectName?: string;
  mapLatitude?: number | null;
  mapLongitude?: number | null;
  /**
   * External multi-select bedroom filter (numeric ids, e.g. `[2, 3]`).
   * When supplied, the Available Units rows shown in the table —
   * along with their derived stack / unit-type popover options — are
   * restricted to units whose bedroom count matches one of the values.
   * Mirrors the agent-side `topViewingFilteredAvailableUnits` flow in
   * `SuperPickerApp.tsx`. `[]` / omitted = no filter.
   */
  bedroomFilter?: number[];
  /**
   * External multi-select unit-type filter (descriptions, e.g.
   * `['3 Bedroom']`). Same source/contract as `bedroomFilter`. Matched
   * against `unitTypeDescription || unitType` per unit so the dropdown
   * value the parent's `SitePlanSectionDetail` produces lines up.
   */
  unitTypeFilter?: string[];
  /**
   * Expanded set of unit-type CODES that map to the selected
   * descriptions in `unitTypeFilter`. Required because some upstream
   * rows carry only the code (e.g. `C5-PREMIUM`) with no description,
   * which a description-only filter silently rejects. Mirrors the
   * agent's `sitePlanUnitTypeFilterExpandedUnitTypes`. Optional /
   * defaults to an empty set so legacy callers keep working.
   */
  unitTypeFilterCodes?: Set<string>;
  /**
   * External multi-select stack filter (e.g. `['24']`, `['ODD']`).
   * When supplied, Available Units rows are restricted to units whose
   * stack matches one of the selected values. Mirrors the agent-side
   * `sitePlanStackFilter` applied in `topViewingFilteredAvailableUnits`.
   * `[]` / omitted = no filter.
   */
  stackFilter?: string[];
  /** When set, tower-view cells in the block dialog show a compare-cart icon. */
  isInCompareCart?: (unitKey: string, projectId?: string | null) => boolean;
}

type TabKey = 'available' | 'bestPx' | 'bestPsf';

const PAGE_SIZE = 15;

/**
 * Penthouse sentinel + detection helpers — mirror the agent-side
 * `PENTHOUSE_FILTER_VALUE` / `isPenthouseUnit` in
 * `TopViewingSitePlanDetail.tsx`. Used so the Bedroom filter's
 * Penthouse option (`-1`) matches PH cells in the Available Units
 * table and PH buckets in the Best PX / Best PSF tables.
 */
const PENTHOUSE_FILTER_VALUE = -1;
const isPenthouseText = (value: unknown): boolean => {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return false;
  return (
    raw.includes('penthouse')
    || raw.includes('penthhouse')
    || /\bph\b/.test(raw)
    || raw.endsWith('ph')
  );
};
const isPenthouseUnit = (u: {
  unitType?: string | null;
  unitTypeDescription?: string | null;
} | null | undefined): boolean =>
  !!u && (isPenthouseText(u.unitTypeDescription) || isPenthouseText(u.unitType));

// Column-visibility config for the public Available Units table. Mirrors the
// agent app's `TOP_VIEWING_AVAILABLE_UNIT_COLUMN_KEYS` slice but limited to
// the columns the public table actually renders. The label formatter mirrors
// `formatColumnVisibilityLabel` from `TopViewingSitePlanDetail.tsx`.
const AVAILABLE_COLUMN_KEYS = [
  'projectName',
  'unit',
  'blockNumber',
  'floor',
  'stack',
  'unitType',
  'bedroom',
  'bathroom',
  'areaSqft',
  'priceDisplay',
  'psfDisplay',
  'distance',
] as const;
type AvailableColumnKey = (typeof AVAILABLE_COLUMN_KEYS)[number];
type SortDirection = 'default' | 'asc' | 'desc';
interface AvailableFilters {
  projectName: string;
  unit: string;
  blockNumber: string;
  floorMin: string;
  floorMax: string;
  stackSelected: string[];
  unitTypeSelected: string[];
  bedroomMin: string;
  bedroomMax: string;
  bathroomMin: string;
  bathroomMax: string;
  areaMin: string;
  areaMax: string;
  priceMin: string;
  priceMax: string;
  psfMin: string;
  psfMax: string;
  distanceMin: string;
  distanceMax: string;
}

const NUMERIC_AVAILABLE_COLUMNS = new Set<AvailableColumnKey>([
  'floor',
  'stack',
  'bedroom',
  'bathroom',
  'areaSqft',
  'priceDisplay',
  'psfDisplay',
  'distance',
]);

const formatAvailableColumnLabel = (column: string): string => {
  switch (column) {
    case 'projectName':
      return 'Project Name';
    case 'unit':
      return 'Unit';
    case 'blockNumber':
      return 'Block';
    case 'floor':
      return 'Floor';
    case 'stack':
      return 'Stack';
    case 'unitType':
      return 'Unit Type';
    case 'bedroom':
      return 'Bedroom';
    case 'bathroom':
      return 'Bath';
    case 'areaSqft':
      return 'Area';
    case 'priceDisplay':
      return 'Price';
    case 'psfDisplay':
      return 'PSF';
    case 'distance':
      return 'Distance';
    default:
      return column;
  }
};

const createDefaultAvailableColumnVisibility = (): ColumnVisibilityState =>
  AVAILABLE_COLUMN_KEYS.reduce<ColumnVisibilityState>((acc, k) => {
    acc[k] = !['projectName', 'blockNumber', 'distance'].includes(k);
    return acc;
  }, {});

const createDefaultAvailableFilters = (): AvailableFilters =>
  ({
    projectName: '',
    unit: '',
    blockNumber: '',
    floorMin: '',
    floorMax: '',
    stackSelected: [],
    unitTypeSelected: [],
    bedroomMin: '',
    bedroomMax: '',
    bathroomMin: '',
    bathroomMax: '',
    areaMin: '',
    areaMax: '',
    priceMin: '',
    priceMax: '',
    psfMin: '',
    psfMax: '',
    distanceMin: '',
    distanceMax: '',
  });

const fmtArea = (v: number | null | undefined): string =>
  v == null ? '-' : v.toLocaleString('en-SG');
const fmtMaskedDollar = (s: string | null | undefined): string =>
  s ? `$${s}` : '-';
const fmtMasked = (s: string | null | undefined): string => s ?? '-';

/**
 * Format an unmasked numeric price as the same `$1,234,567` shape the
 * masked helpers emit. Returns `null` when the value is missing so callers
 * can fall back to the masked string. Always rounded to the nearest whole
 * dollar to match the backend's `maskNumber` rounding rule.
 */
const formatRevealedDollar = (value: number | null | undefined): string | null => {
  if (value == null || !Number.isFinite(value) || value <= 0) return null;
  return `$${Math.round(value).toLocaleString('en-SG')}`;
};

/**
 * Inject a revealed numeric value into a backend-supplied masked-price
 * string column. Returns `revealedFormatted` when present, otherwise the
 * original masked string (so callers can keep using the same field shape).
 */
const overrideMaskedString = (
  masked: string | null | undefined,
  revealed: number | null | undefined,
): string | null => {
  if (revealed != null && Number.isFinite(revealed) && revealed > 0) {
    return Math.round(revealed).toLocaleString('en-SG');
  }
  return masked ?? null;
};

const parseComparableNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

const getAvailableRawValue = (row: AvailableRow, column: AvailableColumnKey): string | number => {
  switch (column) {
    case 'unit':
      return row.unitNumber;
    case 'distance':
      return row.distanceDisplay;
    default:
      return row[column] ?? '';
  }
};

const getRangeFilterKeys = (
  column: AvailableColumnKey,
): { min: keyof AvailableFilters; max: keyof AvailableFilters } | null => {
  switch (column) {
    case 'floor':
      return { min: 'floorMin', max: 'floorMax' };
    case 'bedroom':
      return { min: 'bedroomMin', max: 'bedroomMax' };
    case 'bathroom':
      return { min: 'bathroomMin', max: 'bathroomMax' };
    case 'areaSqft':
      return { min: 'areaMin', max: 'areaMax' };
    case 'priceDisplay':
      return { min: 'priceMin', max: 'priceMax' };
    case 'psfDisplay':
      return { min: 'psfMin', max: 'psfMax' };
    case 'distance':
      return { min: 'distanceMin', max: 'distanceMax' };
    default:
      return null;
  }
};

const hasAvailableColumnFilter = (filters: AvailableFilters, column: AvailableColumnKey): boolean => {
  const rangeKeys = getRangeFilterKeys(column);
  if (rangeKeys) {
    return String(filters[rangeKeys.min]).trim() !== '' || String(filters[rangeKeys.max]).trim() !== '';
  }
  if (column === 'stack') return filters.stackSelected.length > 0;
  if (column === 'unitType') return filters.unitTypeSelected.length > 0;
  if (column === 'projectName' || column === 'unit' || column === 'blockNumber') {
    return filters[column].trim() !== '';
  }
  return false;
};

const getRowNumericValue = (row: AvailableRow, column: AvailableColumnKey): number | null => {
  switch (column) {
    case 'floor':
      return row.floorNum;
    case 'bedroom':
      return row.bedroomNum;
    case 'bathroom':
      return row.bathroomNum;
    case 'areaSqft':
      return row.areaNum;
    case 'priceDisplay':
      return row.priceNum;
    case 'psfDisplay':
      return row.psfNum;
    case 'distance':
      return row.distanceNum;
    default:
      return null;
  }
};

const passesAvailableColumnFilter = (
  row: AvailableRow,
  column: AvailableColumnKey,
  filters: AvailableFilters,
): boolean => {
  const rangeKeys = getRangeFilterKeys(column);
  if (rangeKeys) {
    const minRaw = String(filters[rangeKeys.min]).trim();
    const maxRaw = String(filters[rangeKeys.max]).trim();
    const min = minRaw === '' ? null : parseComparableNumber(minRaw);
    const max = maxRaw === '' ? null : parseComparableNumber(maxRaw);
    if (min == null && max == null) return true;
    const value = getRowNumericValue(row, column);
    if (min != null && (value == null || value < min)) return false;
    if (max != null && (value == null || value > max)) return false;
    return true;
  }
  if (column === 'stack') {
    if (filters.stackSelected.length === 0) return true;
    if (row.stackNum == null) return false;
    return filters.stackSelected.includes(String(row.stackNum));
  }
  if (column === 'unitType') {
    if (filters.unitTypeSelected.length === 0) return true;
    const unitType = (row.unitType || '').trim();
    if (!unitType) return false;
    return filters.unitTypeSelected.includes(unitType);
  }
  if (column === 'projectName' || column === 'unit' || column === 'blockNumber') {
    const filter = filters[column].trim().toLowerCase();
    if (!filter) return true;
    return String(getAvailableRawValue(row, column)).toLowerCase().includes(filter);
  }
  return true;
};

const hasAnyAvailableFilter = (filters: AvailableFilters): boolean =>
  AVAILABLE_COLUMN_KEYS.some((column) => hasAvailableColumnFilter(filters, column));

const getSortIndicatorIcon = (direction: SortDirection) => {
  if (direction === 'default') return <MenuIcon fontSize="small" />;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      {direction === 'asc'
        ? <ArrowUpwardIcon sx={{ fontSize: '0.85rem' }} />
        : <ArrowDownwardIcon sx={{ fontSize: '0.85rem' }} />}
      <MenuIcon fontSize="small" />
    </Box>
  );
};

/** Header menu icon styling — accent when that column has an active filter (agent comp-table parity). */
const getAvailableIconButtonSx = (
  column: AvailableColumnKey,
  hasColumnFilter: (column: AvailableColumnKey) => boolean,
) => ({
  p: 0.25,
  color: hasColumnFilter(column) ? 'var(--color-accent)' : 'rgba(var(--color-text-rgb), 0.5)',
  '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.1)' },
});

// ---------------------------------------------------------------------------
// AVAILABLE UNITS — masked rows from `tower-view-units` only.
// ---------------------------------------------------------------------------

interface AvailableRow {
  key: string;
  projectName: string;
  unitNumber: string;
  blockNumber: string;
  floor: string | number;
  stack: string | number;
  unitType: string;
  bedroom: number | string;
  bathroom: number | string;
  areaSqft: number | string;
  /** Pre-formatted price ("$1,XXX,XXX") or numeric mock value. */
  priceDisplay: string;
  psfDisplay: string;
  distanceDisplay: string;
  status: string;
  /** Raw numeric values used purely for filtering / sorting. */
  floorNum: number | null;
  stackNum: number | null;
  bedroomNum: number | null;
  bathroomNum: number | null;
  areaNum: number | null;
  priceNum: number | null;
  psfNum: number | null;
  distanceNum: number | null;
}

const fromPublicUnits = (units: PublicTowerViewUnit[]): AvailableRow[] =>
  units.map((u) => {
    const parsed = parseUnitNumber(u.unitNumber, u.floorNumber);
    // Ground-floor ("G") units use floor 1 only for tower-grid placement;
    // Available Units should display floor as "-".
    const isGround = isGroundFloorNumber(u.floorNumber);
    const floor = isGround ? null : parsed.floor;
    const stack = parsed.stack;
    return {
      key: u.key,
      projectName: u.projectName || '',
      unitNumber: u.unitNumber,
      blockNumber: u.blockNumber || '',
      floor: floor != null && floor !== 0 ? floor : '-',
      stack: stack != null && stack !== 0 ? stack : '-',
      unitType: u.unitTypeDescription || u.unitType || '',
      bedroom: u.bedroom ?? '-',
      bathroom: u.bathroom ?? '-',
      areaSqft: u.areaSqft ?? '-',
      priceDisplay: fmtMaskedDollar(u.price),
      psfDisplay: fmtMaskedDollar(u.psf),
      distanceDisplay: typeof u.distance === 'number' ? `${u.distance.toFixed(2)} km` : '-',
      status: u.unitStatus ?? '',
      floorNum: floor,
      stackNum: stack,
      bedroomNum: Number.isFinite(u.bedroom) ? u.bedroom : null,
      bathroomNum: Number.isFinite(u.bathroom) ? u.bathroom : null,
      areaNum: Number.isFinite(u.areaSqft) ? u.areaSqft : null,
      priceNum: parseComparableNumber(u.price),
      psfNum: parseComparableNumber(u.psf),
      distanceNum: typeof u.distance === 'number' ? u.distance : null,
    };
  });

const isUnitSold = (status: string): boolean =>
  (status || '').toString().toLowerCase().includes('sold');

// ---------------------------------------------------------------------------

/**
 * Builds a `PublicUnitForMenu` from a tower-view row. The tower-view rows
 * already carry `floorplan` + `key`, so this is a straight projection.
 */
const towerViewToMenuUnit = (
  u: PublicTowerViewUnit,
  projectId: string | null,
): PublicUnitForMenu => ({
  unitKey: u.key,
  // `projectId` is required for the FloorPlanModal + reveal-prices flow
  // to find this unit's revealed entry in `RevealedPricesContext`. The
  // tower-view payload is per-project so the section's own `projectId`
  // is the source of truth.
  projectId,
  projectName: u.projectName,
  unitNumber: u.unitNumber,
  blockNumber: u.blockNumber,
  bedroom: Number.isFinite(u.bedroom) ? u.bedroom : null,
  bathroom: Number.isFinite(u.bathroom) ? u.bathroom : null,
  areaSqft: Number.isFinite(u.areaSqft) ? u.areaSqft : null,
  unitStatus: u.unitStatus,
  floorplan: u.floorplan,
  floorNumber: u.floorNumber ?? null,
  priceDisplay: u.price ? `$${u.price}` : null,
  psfDisplay: u.psf ? `$${u.psf} PSF` : null,
});

const isTowerViewUnitRevealed = (
  unit: PublicTowerViewUnit,
  revealedUnits: Record<string, RevealedTowerViewUnitPrices> | null | undefined,
): boolean => {
  if (!revealedUnits) return false;
  if (unit.key && revealedUnits[unit.key]) return true;
  const synthetic = buildPublicUnitSyntheticKey(unit.blockNumber, unit.unitNumber);
  return Boolean(revealedUnits[synthetic]);
};

const useShareTokens = () =>
  React.useSyncExternalStore(subscribeShareTokens, getShareTokens, () => null);

export const AvailableUnitsSection: React.FC<AvailableUnitsSectionProps> = ({
  projectId = null,
  liveAvailableUnits = null,
  onUnitClick,
  bestByPrice = null,
  bestByPsf = null,
  bedroomFilter = [],
  unitTypeFilter = [],
  unitTypeFilterCodes,
  stackFilter = [],
  sitePlanImageUrl = null,
  sitePlanBlocks = [],
  sitePlanBounds = null,
  stackInfos = [],
  stackSoldPercentages = null,
  projectName = '',
  mapLatitude = null,
  mapLongitude = null,
  isInCompareCart,
}) => {
  const mobileOptimizedStyles = useMobileOptimizedStyles();
  const [tab, setTab] = useState<TabKey>('available');
  const [page, setPage] = useState(1);
  const [collapsed, setCollapsed] = useState(false);

  // Column-visibility state for the available-units table (mirrors the
  // agent's `availableUnitColumnVisibility` + `availableUnitColumnsAnchor`
  // pair from `TopViewingSitePlanDetail.tsx`).
  const [availableColumnsAnchor, setAvailableColumnsAnchor] =
    useState<HTMLElement | null>(null);
  const [availableColumnVisibility, setAvailableColumnVisibility] =
    useState<ColumnVisibilityState>(() => createDefaultAvailableColumnVisibility());
  const [towerViewUnit, setTowerViewUnit] = useState<PublicTowerViewUnit | null>(null);
  // Site-plan filters lifted out of `SitePlanSectionDetail` so the
  // dialog's sibling `TowerViewSection` shares the same Bedroom +
  // Unit Type selection (mirrors the agent-side plumbing).
  const [towerDialogBedroomFilter, setTowerDialogBedroomFilter] = useState<number[]>([]);
  const [towerDialogUnitTypeFilter, setTowerDialogUnitTypeFilter] = useState<string[]>([]);
  // Shared Stack selection so the dialog's Tower View Stack dropdown also
  // filters + pulses the matching site-plan dots (agent parity).
  const [towerDialogStackFilter, setTowerDialogStackFilter] = useState<string[]>([]);
  const [availablePopoverAnchor, setAvailablePopoverAnchor] =
    useState<{ element: HTMLElement; column: AvailableColumnKey } | null>(null);
  const [availableSortConfig, setAvailableSortConfig] =
    useState<{ column: AvailableColumnKey | null; direction: SortDirection }>({
      column: null,
      direction: 'default',
    });
  const [availableFilters, setAvailableFilters] =
    useState<AvailableFilters>(() => createDefaultAvailableFilters());
  const toggleAvailableColumn = useCallback((column: string) => {
    setAvailableColumnVisibility((prev) => ({
      ...prev,
      [column]: prev[column] === false,
    }));
  }, []);
  const resetAvailableColumns = useCallback(() => {
    setAvailableColumnVisibility(createDefaultAvailableColumnVisibility());
  }, []);
  const isAvailableColumnVisible = useCallback(
    (column: AvailableColumnKey) => availableColumnVisibility[column] !== false,
    [availableColumnVisibility],
  );
  const getAvailableSortDirection = useCallback(
    (column: AvailableColumnKey): SortDirection =>
      availableSortConfig.column === column ? availableSortConfig.direction : 'default',
    [availableSortConfig],
  );
  const hasAvailableFilter = useCallback(
    (column: AvailableColumnKey) => hasAvailableColumnFilter(availableFilters, column),
    [availableFilters],
  );

  // ----- Reveal-prices flow ---------------------------------------------
  // Per-row reveal: the unit-actions menu (`UnitActionsMenu`) renders a
  // "Reveal prices" item that the parent (`ProjectDetailPage`) wires to a
  // token-spend dialog. When a unit's prices are revealed the unmasked
  // map is stored in the `RevealedPrices` context under
  // `(towerView, projectId)` keyed by synthetic unit key, and we read
  // from it here to swap the masked strings for real numbers in every
  // row that shows that unit (Available, Best PX, Best PSF).
  const revealedUnits = useRevealedSection('towerView', projectId);
  const { setRevealed } = useRevealedPrices();
  const shareTokens = useShareTokens();

  // Apply the parent-supplied site-plan filters before turning the
  // live units into table rows. Mirrors the agent-side
  // `unitMatchesFilter` recipe in `TopViewingSitePlanDetail.tsx`:
  //   - Bedroom: PENTHOUSE_FILTER_VALUE (-1) matches penthouse units;
  //     a numeric value matches ONLY when the unit is not a penthouse.
  //   - Unit-type: matches when the unit's description is in the
  //     selected descriptions OR when its code is in the expanded
  //     code-set (`unitTypeFilterCodes`), so PREMIUM rows whose
  //     payload carries only a code still match the filter.
  // Empty filter arrays → no restriction.
  const filteredLiveUnits = useMemo<PublicTowerViewUnit[]>(() => {
    if (!liveAvailableUnits || liveAvailableUnits.length === 0) return [];
    // Always exclude sold and not-released units from the Available Units
    // list, regardless of whether any site-plan filter is active. Mirrors the
    // agent-side tower-view "Available" view, which never surfaces SOLD rows in
    // this table (sold/not-released units stay visible in the tower grid for
    // context but not in the per-row Available Units listing).
    const available = liveAvailableUnits.filter(
      (u) => !isUnitSold(u.unitStatus ?? '') && !isUnitStatusNotReleased(u.unitStatus),
    );
    const hasBedroomFilter = bedroomFilter.length > 0;
    const hasUnitTypeFilter = unitTypeFilter.length > 0;
    const hasStackFilter = stackFilter.length > 0;
    if (!hasBedroomFilter && !hasUnitTypeFilter && !hasStackFilter) return available;

    const matchesStack = (u: PublicTowerViewUnit): boolean => {
      if (!hasStackFilter) return true;
      const parsed = parseUnitNumber(u.unitNumber, u.floorNumber);
      if (parsed.stack == null) return false;
      const stack = String(parsed.stack);
      const stackNum = parsed.stack;
      return stackFilter.some((sel) => {
        const normalized = String(sel).trim().toUpperCase();
        if (normalized === 'ODD') return Number.isFinite(stackNum) && stackNum % 2 === 1;
        if (normalized === 'EVEN') return Number.isFinite(stackNum) && stackNum % 2 === 0;
        return String(sel).trim() === stack || Number(sel) === stackNum;
      });
    };

    return available.filter((u) => {
      if (!matchesStack(u)) return false;
      const unitIsPenthouse = isPenthouseUnit(u);
      if (hasBedroomFilter) {
        const matchesBedroom = bedroomFilter.some((v) =>
          v === PENTHOUSE_FILTER_VALUE
            ? unitIsPenthouse
            : !unitIsPenthouse
                && Number.isFinite(u.bedroom)
                && u.bedroom === v,
        );
        if (!matchesBedroom) return false;
      }
      if (hasUnitTypeFilter) {
        const desc = (u.unitTypeDescription || '').trim();
        const code = (u.unitType || '').trim();
        const matchesDesc = !!desc && unitTypeFilter.includes(desc);
        const matchesCode =
          !!code && !!unitTypeFilterCodes && unitTypeFilterCodes.has(code);
        if (!matchesDesc && !matchesCode) return false;
      }
      return true;
    });
  }, [liveAvailableUnits, bedroomFilter, unitTypeFilter, unitTypeFilterCodes, stackFilter]);

  const maskedRevealUnitKeys = useMemo(
    () => filteredLiveUnits
      .filter((unit) => !isTowerViewUnitRevealed(unit, revealedUnits))
      .map((unit) => unit.key)
      .filter((key) => key.trim().length > 0),
    [filteredLiveUnits, revealedUnits],
  );

  const availableUnitCount = filteredLiveUnits.length;
  const allAvailablePricesRevealed = useMemo(
    () => availableUnitCount > 0
      && filteredLiveUnits.every((unit) => isTowerViewUnitRevealed(unit, revealedUnits)),
    [availableUnitCount, filteredLiveUnits, revealedUnits],
  );
  const canShowRevealAll = Boolean(
    getPublicShareUuid()
    && projectId
    && availableUnitCount > 0
    && !allAvailablePricesRevealed
    && maskedRevealUnitKeys.length > 0
    && shareTokens
    && shareTokens.remaining >= availableUnitCount,
  );

  const revealAll = useTokenSpend<{ units: Record<string, RevealedTowerViewUnitPrices> }>({
    title: 'Reveal All',
    actionLabel: 'Yes',
    tokenCost: maskedRevealUnitKeys.length,
    description: (
      <>
        Are you sure you want to spend <strong>{maskedRevealUnitKeys.length}</strong> token
        {maskedRevealUnitKeys.length === 1 ? '' : 's'} to reveal all remaining masked available
        unit prices for <strong>{projectName || 'this project'}</strong>?
      </>
    ),
    spend: async () => {
      const uuid = getPublicShareUuid();
      if (!uuid) throw new Error('Missing share context.');
      if (!projectId) throw new Error('Missing projectId.');
      if (maskedRevealUnitKeys.length === 0) throw new Error('No masked units to reveal.');
      const response = await revealSection(uuid, {
        section: 'towerView',
        projectId,
        unitKeys: maskedRevealUnitKeys,
      });
      if (response.section !== 'towerView') {
        throw new Error('Unexpected reveal response.');
      }
      return {
        remainingTokens: response.remainingTokens,
        maxTokens: response.maxTokens,
        data: { units: response.units },
      };
    },
    onConfirmed: ({ data }) => {
      if (!data || !projectId) return;
      const existing = revealedUnits ?? {};
      setRevealed('towerView', projectId, { ...existing, ...data.units });
    },
  });

  // Available-units rows: live rows including all statuses. When a row's
  // unit has been revealed, swap its masked `priceDisplay` / `psfDisplay`
  // for the real numeric value.
  const availableRows = useMemo<AvailableRow[]>(() => {
    if (filteredLiveUnits.length === 0) return [];
    const base = fromPublicUnits(filteredLiveUnits);
    if (!revealedUnits) return base;
    return base.map((row) => {
      const liveUnit = filteredLiveUnits.find((unit) => unit.key === row.key)
        ?? filteredLiveUnits.find((unit) => unit.unitNumber === row.unitNumber);
      const revealed = liveUnit && isTowerViewUnitRevealed(liveUnit, revealedUnits)
        ? (revealedUnits[liveUnit.key]
          ?? revealedUnits[buildPublicUnitSyntheticKey(liveUnit.blockNumber, liveUnit.unitNumber)])
        : revealedUnits[row.key];
      if (!revealed) return row;
      const priceDisplay = formatRevealedDollar(revealed.price);
      const psfDisplay = formatRevealedDollar(revealed.psf);
      return {
        ...row,
        priceDisplay: priceDisplay ?? row.priceDisplay,
        psfDisplay: psfDisplay ?? row.psfDisplay,
        priceNum: revealed.price ?? row.priceNum,
        psfNum: revealed.psf ?? row.psfNum,
      };
    });
  }, [filteredLiveUnits, revealedUnits]);

  const filteredSortedRows = useMemo(() => {
    const hasActiveFilters = hasAnyAvailableFilter(availableFilters);
    const filtered = hasActiveFilters
      ? availableRows.filter((row) =>
        AVAILABLE_COLUMN_KEYS.every((column) => passesAvailableColumnFilter(row, column, availableFilters)),
      )
      : availableRows;
    const { column, direction } = availableSortConfig;
    if (!column || direction === 'default') return filtered;
    return [...filtered].sort((a, b) => {
      const aRaw = getAvailableRawValue(a, column);
      const bRaw = getAvailableRawValue(b, column);
      const aNum = NUMERIC_AVAILABLE_COLUMNS.has(column) ? parseComparableNumber(aRaw) : null;
      const bNum = NUMERIC_AVAILABLE_COLUMNS.has(column) ? parseComparableNumber(bRaw) : null;
      const comparison = aNum != null && bNum != null
        ? aNum - bNum
        : String(aRaw).localeCompare(String(bRaw), undefined, { numeric: true, sensitivity: 'base' });
      return direction === 'asc' ? comparison : -comparison;
    });
  }, [availableFilters, availableRows, availableSortConfig]);

  // Lookup by unitNumber so the Best PX / Best PSF rows (which lack
  // floorplan + key + project name) can be enriched back into a
  // `PublicUnitForMenu` for the unit-actions menu.
  const liveUnitByNumber = useMemo<Map<string, PublicTowerViewUnit>>(() => {
    const m = new Map<string, PublicTowerViewUnit>();
    (liveAvailableUnits ?? []).forEach((u) => m.set(u.unitNumber, u));
    return m;
  }, [liveAvailableUnits]);

  const handleAvailableRowClick = useCallback(
    (r: AvailableRow, e: React.MouseEvent<HTMLElement>) => {
      if (!onUnitClick) return;
      const live = liveUnitByNumber.get(r.unitNumber);
      if (!live) return;
      onUnitClick(towerViewToMenuUnit(live, projectId), e.clientX, e.clientY);
    },
    [liveUnitByNumber, onUnitClick, projectId],
  );

  const handleOpenTowerView = useCallback(
    (r: AvailableRow) => {
      const live = liveUnitByNumber.get(r.unitNumber);
      if (live) setTowerViewUnit(live);
    },
    [liveUnitByNumber],
  );

  const towerViewDialogUnits = useMemo(() => {
    if (!towerViewUnit) return [];
    const block = towerViewUnit.blockNumber;
    if (!block) return liveAvailableUnits ?? [];
    return (liveAvailableUnits ?? []).filter((u) => u.blockNumber === block);
  }, [liveAvailableUnits, towerViewUnit]);

  const handleBestRowClick = useCallback(
    (u: PublicBestByUnitTypeRow, e: React.MouseEvent<HTMLElement>) => {
      if (!onUnitClick) return;
      const live = liveUnitByNumber.get(u.unitNumber);
      if (live) {
        onUnitClick(towerViewToMenuUnit(live, projectId), e.clientX, e.clientY);
        return;
      }
      // Fallback when the unit isn't in the live tower-view feed (e.g. SOLD).
      onUnitClick(
        {
          unitKey: `best:${u.unitNumber}`,
          projectId,
          projectName: '',
          unitNumber: u.unitNumber,
          blockNumber: u.blockNumber || null,
          bedroom: null,
          bathroom: null,
          areaSqft: u.totalAreaSqft ?? null,
          unitStatus: null,
          floorplan: null,
          priceDisplay: u.sellingPrice ? `$${u.sellingPrice}` : null,
          psfDisplay: u.psf ? `$${u.psf} PSF` : null,
        },
        e.clientX,
        e.clientY,
      );
    },
    [liveUnitByNumber, onUnitClick, projectId],
  );

  useEffect(() => {
    setPage(1);
  }, [tab, availableFilters, availableSortConfig, bestByPrice, bestByPsf]);

  const pageCount = Math.max(1, Math.ceil(filteredSortedRows.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(page, 1), pageCount);
  const start = (currentPage - 1) * PAGE_SIZE;
  const visible = filteredSortedRows.slice(start, start + PAGE_SIZE);
  const hasActiveAvailableFilters = hasAnyAvailableFilter(availableFilters);
  const rangeLabel = `${filteredSortedRows.length === 0 ? 0 : start + 1}-${Math.min(
    start + PAGE_SIZE,
    filteredSortedRows.length,
  )} of ${filteredSortedRows.length}`;

  // Best-by-unit-type tab payload selection. When prices have been revealed
  // we rewrite each row's masked string fields in-place so the existing
  // render call sites (~20 of them, using `moneyOrDash` / `psfWithSuffix`)
  // pick up the unmasked numbers without further changes. The synthetic
  // `key` is reconstructed from `(blockNumber, unitNumber)` — matches the
  // backend's `buildSyntheticKey`.
  const overlayBestPayload = useCallback(
    (payload: PublicBestByUnitTypePayload | null | undefined): PublicBestByUnitTypePayload | null => {
      if (!payload) return payload ?? null;
      if (!revealedUnits) return payload;
      return {
        ...payload,
        unitTypes: payload.unitTypes.map((bucket) => ({
          ...bucket,
          units: bucket.units.map((u: PublicBestByUnitTypeRow) => {
            const key = `${(u.blockNumber || 'B').replace(/\s+/g, '')}-${(u.unitNumber || '').replace(/\s+/g, '')}`;
            const revealed = revealedUnits[key];
            if (!revealed) return u;
            return {
              ...u,
              sellingPrice: overrideMaskedString(u.sellingPrice, revealed.price),
              psf: overrideMaskedString(u.psf, revealed.psf),
              sellingPriceNps: overrideMaskedString(u.sellingPriceNps, revealed.priceNps),
              sellingPriceDps: overrideMaskedString(u.sellingPriceDps, revealed.priceDps),
              psfNps: overrideMaskedString(u.psfNps, revealed.psfNps),
              psfDps: overrideMaskedString(u.psfDps, revealed.psfDps),
            };
          }),
        })),
      };
    },
    [revealedUnits],
  );
  // Best PX / Best PSF tabs render the backend payload as-is (only the
  // revealed-prices overlay is applied). Mirrors the agent
  // (`TopViewingSitePlanDetail.tsx :: renderTopViewingBestBedroomContent`,
  // ~3365-3567) which renders `bestByUnitTypeData.unitTypes` directly
  // — the site-plan bedroom / unit-type filter is intentionally NOT
  // applied to these tabs, only to the Available Units list.
  const effectiveBestByPrice = useMemo(() => overlayBestPayload(bestByPrice), [overlayBestPayload, bestByPrice]);
  const effectiveBestByPsf = useMemo(() => overlayBestPayload(bestByPsf), [overlayBestPayload, bestByPsf]);

  const bestPayload =
    tab === 'bestPsf' ? effectiveBestByPsf : tab === 'bestPx' ? effectiveBestByPrice : null;
  const isPsfMetric = tab === 'bestPsf';

  return (
    <Card sx={{ mb: 2, position: 'relative', ...mobileOptimizedStyles }}>
      <CardContent sx={{ p: 0 }}>
        {/* Header: tabs + actions */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            px: { xs: 1.5, sm: 2 },
            pt: 1.5,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v as TabKey)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 0,
              p: 0.375,
              border: '1px solid rgba(var(--color-text-rgb), 0.12)',
              borderRadius: 1.5,
              bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTabs-flexContainer': { gap: 0.5 },
              '& .MuiTab-root': {
                minHeight: 38,
                minWidth: 'unset',
                px: 1.5,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1,
                border: '1px solid transparent',
                color: 'rgba(var(--color-text-rgb), 0.72)',
                '&.Mui-selected': {
                  color: 'var(--color-text-strong)',
                  bgcolor: 'rgba(var(--color-accent-rgb), 0.14)',
                  borderColor: 'rgba(var(--color-accent-rgb), 0.32)',
                  boxShadow: 'inset 0 -2px 0 var(--color-accent)',
                },
              },
            }}
          >
            <Tab value="available" label={`Available Units (${availableRows.length})`} />
            <Tab value="bestPx" label="BEST PX BEDROOM TYPE" />
            <Tab value="bestPsf" label="BEST PSF BEDROOM TYPE" />
          </Tabs>
          <Box sx={{ flex: 1 }} />
          {tab === 'available' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'flex-end', sm: 'flex-start' },
              }}
            >
              {canShowRevealAll && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<TokenIcon sx={{ color: 'var(--color-accent)' }} />}
                  onClick={revealAll.request}
                  sx={{
                    color: 'var(--color-accent)',
                    borderColor: 'var(--color-accent)',
                    fontWeight: 600,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: 'var(--color-accent)',
                      bgcolor: 'rgba(var(--color-accent-rgb), 0.08)',
                    },
                  }}
                >
                  Reveal All
                </Button>
              )}
              <ColumnVisibilityMenu
                anchorEl={availableColumnsAnchor}
                columns={AVAILABLE_COLUMN_KEYS}
                getLabel={formatAvailableColumnLabel}
                onClose={() => setAvailableColumnsAnchor(null)}
                onOpen={(event) => setAvailableColumnsAnchor(event.currentTarget)}
                onReset={resetAvailableColumns}
                onToggle={toggleAvailableColumn}
                visibility={availableColumnVisibility}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setAvailableFilters(createDefaultAvailableFilters());
                  setAvailableSortConfig({ column: null, direction: 'default' });
                }}
                sx={{
                  borderColor: 'rgba(var(--color-text-rgb), 0.3)',
                  color: 'var(--color-text-strong)',
                  textTransform: 'none',
                }}
              >
                Clear Filters
              </Button>
            </Box>
          )}
          <IconButton
            size="small"
            onClick={() => setCollapsed((v) => !v)}
            sx={{
              color: 'var(--color-text-strong)',
              transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 200ms ease',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        {!collapsed && (
          <Box sx={{ p: 2 }}>
            {tab === 'available' ? (
              <AvailableUnitsTable
                rows={visible}
                pageCount={pageCount}
                page={currentPage}
                onPageChange={setPage}
                rangeLabel={rangeLabel}
                empty={visible.length === 0}
                emptyLabel={availableRows.length > 0 && hasActiveAvailableFilters ? 'No units match the current filters.' : 'No units to display.'}
                onRowClick={handleAvailableRowClick}
                onOpenTowerView={handleOpenTowerView}
                isColumnVisible={isAvailableColumnVisible}
                onOpenColumnMenu={(event: React.MouseEvent<HTMLElement>, column: AvailableColumnKey) =>
                  setAvailablePopoverAnchor({ element: event.currentTarget, column })
                }
                getSortDirection={getAvailableSortDirection}
                hasColumnFilter={hasAvailableFilter}
              />
            ) : (
              <BestByUnitTypeTable
                payload={bestPayload}
                isPsfMetric={isPsfMetric}
                sitePlanImageUrl={sitePlanImageUrl}
                onRowClick={handleBestRowClick}
              />
            )}
          </Box>
        )}
      </CardContent>
      <Dialog
        open={Boolean(towerViewUnit)}
        onClose={() => setTowerViewUnit(null)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Tower View{projectName ? ` - ${projectName}` : ''}
            {towerViewUnit?.blockNumber ? ` - Blk ${towerViewUnit.blockNumber}` : ''}
          </Typography>
          <IconButton size="small" onClick={() => setTowerViewUnit(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <SitePlanSectionDetail
            projectName={projectName || towerViewUnit?.projectName || ''}
            projectId={projectId}
            onUnitClick={onUnitClick}
            imageUrl={sitePlanImageUrl}
            blocks={sitePlanBlocks}
            bounds={sitePlanBounds}
            stackInfos={stackInfos}
            stackSoldPercentages={stackSoldPercentages}
            mapLatitude={mapLatitude}
            mapLongitude={mapLongitude}
            bedroomFilter={towerDialogBedroomFilter}
            onBedroomFilterChange={setTowerDialogBedroomFilter}
            unitTypeFilter={towerDialogUnitTypeFilter}
            onUnitTypeFilterChange={setTowerDialogUnitTypeFilter}
            stackFilter={towerDialogStackFilter}
          />
          <TowerViewSection
            units={towerViewDialogUnits}
            projectId={projectId}
            onCellClick={onUnitClick}
            isInCompareCart={isInCompareCart}
            bedroomFilter={towerDialogBedroomFilter}
            unitTypeFilter={towerDialogUnitTypeFilter}
            stackFilter={towerDialogStackFilter}
            onStackFilterChange={setTowerDialogStackFilter}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTowerViewUnit(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Popover
        open={Boolean(availablePopoverAnchor)}
        anchorEl={availablePopoverAnchor?.element}
        onClose={() => setAvailablePopoverAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {availablePopoverAnchor && (
          <AvailableColumnPopoverContent
            column={availablePopoverAnchor.column}
            filters={availableFilters}
            stackOptions={Array.from(new Set(availableRows.map((row) => row.stackNum).filter((v): v is number => v != null))).sort((a, b) => a - b).map(String)}
            unitTypeOptions={Array.from(new Set(availableRows.map((row) => (row.unitType || '').trim()).filter(Boolean))).sort()}
            sortDirection={getAvailableSortDirection(availablePopoverAnchor.column)}
            hasFilter={hasAvailableFilter(availablePopoverAnchor.column)}
            onFiltersChange={setAvailableFilters}
            onSortChange={(direction: SortDirection) =>
              setAvailableSortConfig(
                direction === 'default'
                  ? { column: null, direction: 'default' }
                  : { column: availablePopoverAnchor.column, direction },
              )
            }
          />
        )}
      </Popover>
      {revealAll.dialog}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// AVAILABLE UNITS TABLE
// ---------------------------------------------------------------------------

const AVAILABLE_COLUMNS: Array<{
  key: keyof AvailableRow | 'unit' | 'distance';
  label: string;
  align?: 'left' | 'right' | 'center';
  render: (r: AvailableRow, actions: { onOpenTowerView?: (r: AvailableRow) => void }) => React.ReactNode;
}> = [
  {
    key: 'projectName',
    label: 'Project Name',
    render: (r, actions) => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="body2">{r.projectName || '-'}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              actions.onOpenTowerView?.(r);
            }}
            sx={{
              color: 'rgba(var(--color-text-rgb), 0.5)',
              '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.1)' },
            }}
            title="View Tower View"
          >
            <ViewModuleIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    ),
  },
  { key: 'unit', label: 'Unit', render: (r) => r.unitNumber },
  { key: 'blockNumber', label: 'Block', render: (r) => r.blockNumber || '-' },
  { key: 'floor', label: 'Floor', align: 'center', render: (r) => r.floor },
  { key: 'stack', label: 'Stack', align: 'center', render: (r) => r.stack },
  { key: 'unitType', label: 'Unit Type', render: (r) => r.unitType || '-' },
  { key: 'bedroom', label: 'Bedroom', align: 'center', render: (r) => r.bedroom },
  { key: 'bathroom', label: 'Bath', align: 'center', render: (r) => r.bathroom },
  {
    key: 'areaSqft',
    label: 'Area',
    align: 'right',
    render: (r) => (typeof r.areaSqft === 'number' ? fmtArea(r.areaSqft) : r.areaSqft),
  },
  { key: 'priceDisplay', label: 'Price', align: 'right', render: (r) => r.priceDisplay },
  { key: 'psfDisplay', label: 'PSF', align: 'right', render: (r) => r.psfDisplay },
  { key: 'distance', label: 'Distance', align: 'right', render: (r) => r.distanceDisplay },
];

const AvailableUnitsTable: React.FC<{
  rows: AvailableRow[];
  pageCount: number;
  page: number;
  onPageChange: (p: number) => void;
  rangeLabel: string;
  empty: boolean;
  emptyLabel?: string;
  onRowClick?: (r: AvailableRow, e: React.MouseEvent<HTMLElement>) => void;
  onOpenTowerView?: (r: AvailableRow) => void;
  isColumnVisible?: (column: AvailableColumnKey) => boolean;
  onOpenColumnMenu?: (event: React.MouseEvent<HTMLElement>, column: AvailableColumnKey) => void;
  getSortDirection?: (column: AvailableColumnKey) => SortDirection;
  hasColumnFilter?: (column: AvailableColumnKey) => boolean;
}> = ({
  rows,
  pageCount,
  page,
  onPageChange,
  rangeLabel,
  empty,
  emptyLabel = 'No units to display.',
  onRowClick,
  onOpenTowerView,
  isColumnVisible,
  onOpenColumnMenu,
  getSortDirection,
  hasColumnFilter = () => false,
}) => {
  const visibleColumns = AVAILABLE_COLUMNS.filter((c) =>
    isColumnVisible ? isColumnVisible(c.key as AvailableColumnKey) : true,
  );
  return (
  <>
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {visibleColumns.map((c) => (
              <TableCell
                key={c.key as string}
                align={c.align ?? 'left'}
                sx={{
                  bgcolor: 'rgba(var(--color-text-rgb), 0.06)',
                  color: 'var(--color-text-strong)',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: c.align === 'right' ? 'flex-end' : c.align === 'center' ? 'center' : 'flex-start',
                    gap: 0.5,
                  }}
                >
                  {c.label}
                  {c.key !== 'unit' && (
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenColumnMenu?.(event, c.key as AvailableColumnKey);
                      }}
                      sx={getAvailableIconButtonSx(c.key as AvailableColumnKey, hasColumnFilter)}
                    >
                      {getSortIndicatorIcon(getSortDirection?.(c.key as AvailableColumnKey) ?? 'default')}
                    </IconButton>
                  )}
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {empty ? (
            <TableRow>
              <TableCell colSpan={visibleColumns.length} align="center">
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 3 }}
                >
                  {emptyLabel}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((r) => (
              <TableRow
                key={r.key}
                hover
                onClick={(e) => onRowClick?.(r, e)}
                sx={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.04)' },
                }}
              >
                {visibleColumns.map((c) => (
                  <TableCell
                    key={c.key as string}
                    align={c.align ?? 'left'}
                    sx={{
                      color: 'var(--color-text-strong)',
                      whiteSpace: 'nowrap',
                      borderColor: 'rgba(var(--color-text-rgb), 0.08)',
                    }}
                  >
                    {c.render(r, { onOpenTowerView })}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>

    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 2,
        pt: 1,
      }}
    >
      <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
        {rangeLabel}
      </Typography>
      <Pagination
        size="small"
        count={pageCount}
        page={page}
        onChange={(_, p) => onPageChange(p)}
        siblingCount={0}
        boundaryCount={1}
      />
    </Box>
  </>
  );
};

const AvailableColumnPopoverContent: React.FC<{
  column: AvailableColumnKey;
  filters: AvailableFilters;
  stackOptions: string[];
  unitTypeOptions: string[];
  sortDirection: SortDirection;
  hasFilter: boolean;
  onFiltersChange: React.Dispatch<React.SetStateAction<AvailableFilters>>;
  onSortChange: (direction: SortDirection) => void;
}> = ({
  column,
  filters,
  stackOptions,
  unitTypeOptions,
  sortDirection,
  hasFilter,
  onFiltersChange,
  onSortChange,
}) => {
  const rangeKeys = getRangeFilterKeys(column);
  const clearFilter = () => {
    if (rangeKeys) {
      onFiltersChange((prev) => ({ ...prev, [rangeKeys.min]: '', [rangeKeys.max]: '' }));
      return;
    }
    if (column === 'stack') {
      onFiltersChange((prev) => ({ ...prev, stackSelected: [] }));
      return;
    }
    if (column === 'unitType') {
      onFiltersChange((prev) => ({ ...prev, unitTypeSelected: [] }));
      return;
    }
    if (column === 'projectName' || column === 'unit' || column === 'blockNumber') {
      onFiltersChange((prev) => ({ ...prev, [column]: '' }));
    }
  };
  return (
    <Box sx={{ p: 2, minWidth: 260 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1, color: 'var(--color-info)', fontWeight: 500 }}>
          Sort
        </Typography>
        <ToggleButtonGroup
          value={sortDirection}
          exclusive
          fullWidth
          onChange={(_, value: SortDirection | null) => {
            if (value !== null) onSortChange(value);
          }}
          sx={{
            '& .MuiToggleButtonGroup-grouped': {
              border: '1px solid rgba(var(--color-shadow-rgb), 0.12)',
              '&:not(:first-of-type)': {
                borderLeft: '1px solid rgba(var(--color-shadow-rgb), 0.12)',
                marginLeft: 0,
              },
              '&.Mui-selected': {
                backgroundColor: 'var(--color-info)',
                color: 'var(--color-text-strong)',
              },
            },
          }}
        >
          <ToggleButton value="default" sx={{ flex: 1, py: 0.75 }}>Default</ToggleButton>
          <ToggleButton value="asc" sx={{ flex: 1, py: 0.75 }}>Asc</ToggleButton>
          <ToggleButton value="desc" sx={{ flex: 1, py: 0.75 }}>Desc</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" sx={{ color: 'var(--color-info)', fontWeight: 500, mr: 1 }}>
            Filter
          </Typography>
          <FilterIcon sx={{ fontSize: 18, color: 'var(--color-info)' }} />
        </Box>
        {rangeKeys ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Min"
              value={String(filters[rangeKeys.min])}
              onChange={(event) => onFiltersChange((prev) => ({ ...prev, [rangeKeys.min]: event.target.value }))}
            />
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max"
              value={String(filters[rangeKeys.max])}
              onChange={(event) => onFiltersChange((prev) => ({ ...prev, [rangeKeys.max]: event.target.value }))}
            />
          </Box>
        ) : column === 'stack' ? (
          <FormControl fullWidth size="small">
            <Select
              multiple
              displayEmpty
              value={filters.stackSelected}
              onChange={(event) => {
                const rawValue = event.target.value;
                const value = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;
                if (value.includes('__SELECT_ALL__')) {
                  onFiltersChange((prev) => ({
                    ...prev,
                    stackSelected: prev.stackSelected.length === stackOptions.length ? [] : [...stackOptions],
                  }));
                  return;
                }
                onFiltersChange((prev) => ({ ...prev, stackSelected: value }));
              }}
              renderValue={(selected) => {
                const selectedValues = selected as string[];
                if (selectedValues.length === 0) return 'All Stacks';
                if (selectedValues.length === stackOptions.length) return 'All Selected';
                return `${selectedValues.length} selected`;
              }}
            >
              <MenuItem value="__SELECT_ALL__">
                <Checkbox
                  checked={filters.stackSelected.length === stackOptions.length && stackOptions.length > 0}
                  indeterminate={filters.stackSelected.length > 0 && filters.stackSelected.length < stackOptions.length}
                />
                <Typography component="span" sx={{ fontWeight: 600 }}>Select All</Typography>
              </MenuItem>
              {stackOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={filters.stackSelected.includes(option)} />
                  <Typography component="span">{option}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : column === 'unitType' ? (
          <FormControl fullWidth size="small">
            <Select
              multiple
              displayEmpty
              value={filters.unitTypeSelected}
              onChange={(event) => {
                const rawValue = event.target.value;
                const value = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;
                if (value.includes('__SELECT_ALL__')) {
                  onFiltersChange((prev) => ({
                    ...prev,
                    unitTypeSelected: prev.unitTypeSelected.length === unitTypeOptions.length ? [] : [...unitTypeOptions],
                  }));
                  return;
                }
                onFiltersChange((prev) => ({ ...prev, unitTypeSelected: value }));
              }}
              renderValue={(selected) => {
                const selectedValues = selected as string[];
                if (selectedValues.length === 0) return 'All Types';
                if (selectedValues.length === unitTypeOptions.length) return 'All Selected';
                return `${selectedValues.length} selected`;
              }}
            >
              <MenuItem value="__SELECT_ALL__">
                <Checkbox
                  checked={filters.unitTypeSelected.length === unitTypeOptions.length && unitTypeOptions.length > 0}
                  indeterminate={filters.unitTypeSelected.length > 0 && filters.unitTypeSelected.length < unitTypeOptions.length}
                />
                <Typography component="span" sx={{ fontWeight: 600 }}>Select All</Typography>
              </MenuItem>
              {unitTypeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={filters.unitTypeSelected.includes(option)} />
                  <Typography component="span">{option}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            fullWidth
            size="small"
            placeholder={`Filter ${formatAvailableColumnLabel(column)}`}
            value={column === 'projectName' || column === 'unit' || column === 'blockNumber' ? filters[column] : ''}
            onChange={(event) => {
              if (column === 'projectName' || column === 'unit' || column === 'blockNumber') {
                onFiltersChange((prev) => ({ ...prev, [column]: event.target.value }));
              }
            }}
            InputProps={{
              endAdornment: hasFilter ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearFilter} sx={{ p: 0.5 }}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        )}
        <Button
          size="small"
          onClick={clearFilter}
          sx={{ mt: 1 }}
          fullWidth
          disabled={!hasFilter}
        >
          Clear Filter{rangeKeys ? '' : ' (Show All)'}
        </Button>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// BEST PX / BEST PSF BEDROOM TYPE TABLES
// ---------------------------------------------------------------------------

const BestByUnitTypeTable: React.FC<{
  payload: PublicBestByUnitTypePayload | null;
  isPsfMetric: boolean;
  sitePlanImageUrl?: string | null;
  onRowClick?: (u: PublicBestByUnitTypeRow, e: React.MouseEvent<HTMLElement>) => void;
}> = ({ payload, isPsfMetric, sitePlanImageUrl = null, onRowClick }) => {
  if (!payload) {
    return (
      <Box
        sx={{
          width: '100%',
          border: '1px dashed rgba(var(--color-text-rgb), 0.2)',
          borderRadius: 1.5,
          p: 4,
          textAlign: 'center',
          bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
          color: 'rgba(var(--color-text-rgb), 0.65)',
        }}
      >
        <Typography variant="body2">
          {isPsfMetric
            ? 'BEST PSF BEDROOM TYPE will be unlocked after official pricing is released.'
            : 'BEST PX BEDROOM TYPE will be unlocked after official pricing is released.'}
        </Typography>
      </Box>
    );
  }

  const summaryHeading = isPsfMetric ? 'Best PSF unit by type' : 'Cheapest unit by type';
  const isEc = payload.isExecutiveCondo;
  // The summary table mirrors the agent app:
  //   - Best PX  → Unit type | Unit no | Area (sqft) | Price ($) | PSF
  //   - Best PSF → Unit type | Unit no | Area (sqft) | PSF  (no Price column)
  const showSummarySecondaryPsf = !isPsfMetric && payload.showPsfInTowerView !== false;
  const ecPrimaryDpsLabel = isPsfMetric ? 'DPS PSF' : 'DPS Price ($)';
  const ecPrimaryNpsLabel = isPsfMetric ? 'NPS PSF' : 'NPS Price ($)';

  const populatedBuckets = payload.unitTypes.filter((b) => b.units.length > 0);
  const summaryRows = populatedBuckets.map((b) => ({ bucket: b, top: b.units[0] }));

  if (summaryRows.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          border: '1px dashed rgba(var(--color-text-rgb), 0.2)',
          borderRadius: 1.5,
          p: 4,
          textAlign: 'center',
          bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
          color: 'rgba(var(--color-text-rgb), 0.65)',
        }}
      >
        <Typography variant="body2">No units to display.</Typography>
      </Box>
    );
  }

  // PSF rendering helpers (the agent app suffixes per-row PSF with " PSF").
  const psfWithSuffix = (s: string | null | undefined): string => (s ? `$${s} PSF` : '-');
  const moneyOrDash = (s: string | null | undefined): string => fmtMaskedDollar(s);

  return (
    <Box>
      {/* ----- Summary table ----- */}
      <Typography
        variant="subtitle2"
        sx={{ color: 'var(--color-heading)', fontWeight: 600, mb: 1 }}
      >
        {summaryHeading}
      </Typography>
      <TableContainer
        sx={{
          border: '1px solid rgba(var(--color-text-rgb), 0.1)',
          borderRadius: 1,
          overflowX: 'auto',
          mb: 2,
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: 620,
            '& .MuiTableCell-root': {
              py: 0.75,
              px: 1.5,
              color: 'var(--color-text-strong)',
              borderColor: 'rgba(var(--color-text-rgb), 0.1)',
              fontSize: '0.8rem',
              whiteSpace: 'nowrap',
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              bgcolor: 'var(--color-surface-elevated)',
              color: 'var(--color-heading)',
              fontWeight: 600,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Unit type</TableCell>
              <TableCell>Unit no</TableCell>
              <TableCell align="right">Area (sqft)</TableCell>
              {isEc ? (
                <>
                  <TableCell align="right">{ecPrimaryDpsLabel}</TableCell>
                  {showSummarySecondaryPsf && <TableCell align="right">DPS PSF</TableCell>}
                  <TableCell align="right">{ecPrimaryNpsLabel}</TableCell>
                  {showSummarySecondaryPsf && <TableCell align="right">NPS PSF</TableCell>}
                </>
              ) : isPsfMetric ? (
                <TableCell align="right">PSF</TableCell>
              ) : (
                <>
                  <TableCell align="right">Price ($)</TableCell>
                  {showSummarySecondaryPsf && <TableCell align="right">PSF</TableCell>}
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {summaryRows.map(({ bucket, top }) => (
              <TableRow
                key={`${bucket.id}-${top.unitNumber}`}
                hover
                onClick={(e) => onRowClick?.(top, e)}
                sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                <TableCell>{(bucket.description ?? bucket.unitType).replace(/Bedroom/gi, 'Bed')}</TableCell>
                <TableCell>{top.unitNumber}</TableCell>
                <TableCell align="right">{fmtArea(top.totalAreaSqft)}</TableCell>
                {isEc ? (
                  <>
                    <TableCell align="right">
                      {isPsfMetric
                        ? psfWithSuffix(top.psfDps ?? top.psf)
                        : moneyOrDash(top.sellingPriceDps ?? top.sellingPrice)}
                    </TableCell>
                    {showSummarySecondaryPsf && (
                      <TableCell align="right">{psfWithSuffix(top.psfDps ?? top.psf)}</TableCell>
                    )}
                    <TableCell align="right">
                      {isPsfMetric
                        ? psfWithSuffix(top.psfNps ?? top.psf)
                        : moneyOrDash(top.sellingPriceNps ?? top.sellingPrice)}
                    </TableCell>
                    {showSummarySecondaryPsf && (
                      <TableCell align="right">{psfWithSuffix(top.psfNps ?? top.psf)}</TableCell>
                    )}
                  </>
                ) : isPsfMetric ? (
                  <TableCell align="right">{psfWithSuffix(top.psf)}</TableCell>
                ) : (
                  <>
                    <TableCell align="right">{moneyOrDash(top.sellingPrice)}</TableCell>
                    {showSummarySecondaryPsf && (
                      <TableCell align="right">{psfWithSuffix(top.psf)}</TableCell>
                    )}
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ----- Per-unit-type cards + site-map sidebar ----- */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            flex: { md: '1 1 65%' },
            width: '100%',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: 2,
            minWidth: 0,
          }}
        >
          {populatedBuckets.map((bucket) => {
            const label = (bucket.description ?? bucket.unitType).replace(/Bedroom/gi, 'BR');
            return (
              <Box
                key={bucket.id}
                sx={{
                  border: '1px solid rgba(var(--color-text-rgb), 0.1)',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: 'var(--color-surface-elevated)',
                    color: 'var(--color-heading)',
                    fontWeight: 600,
                  }}
                >
                  {label}
                </Typography>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table
                    size="small"
                    sx={{
                      minWidth: 480,
                      '& .MuiTableCell-root': {
                        py: 0.75,
                        px: 1,
                        color: 'var(--color-text-strong)',
                        borderColor: 'rgba(var(--color-text-rgb), 0.1)',
                        fontSize: '0.8rem',
                      },
                      '& .MuiTableHead-root .MuiTableCell-root': {
                        bgcolor: 'var(--color-surface-elevated)',
                        color: 'var(--color-heading)',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>UNIT NO</TableCell>
                        <TableCell>BEDRM TYPE</TableCell>
                        <TableCell>TYPE</TableCell>
                        <TableCell align="right">AREA</TableCell>
                        {isEc ? (
                          <>
                            <TableCell align="right">
                              {isPsfMetric ? 'DPS PSF' : 'DPS PRICE ($)'}
                            </TableCell>
                            {showSummarySecondaryPsf && <TableCell align="right">DPS PSF</TableCell>}
                            <TableCell align="right">
                              {isPsfMetric ? 'NPS PSF' : 'NPS PRICE ($)'}
                            </TableCell>
                            {showSummarySecondaryPsf && <TableCell align="right">NPS PSF</TableCell>}
                          </>
                        ) : isPsfMetric ? (
                          <TableCell align="right">PSF</TableCell>
                        ) : (
                          <>
                            <TableCell align="right">PRICE ($)</TableCell>
                            {showSummarySecondaryPsf && <TableCell align="right">PSF</TableCell>}
                          </>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bucket.units.map((u, idx) => (
                        <TableRow
                          key={`${bucket.id}-${u.unitNumber}-${idx}`}
                          hover
                          onClick={(e) => onRowClick?.(u, e)}
                          sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.unitNumber}</TableCell>
                          <TableCell>{label}</TableCell>
                          <TableCell>{u.type ?? '-'}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {fmtArea(u.totalAreaSqft)}
                          </TableCell>
                          {isEc ? (
                            <>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                {isPsfMetric
                                  ? psfWithSuffix(u.psfDps ?? u.psf)
                                  : moneyOrDash(u.sellingPriceDps ?? u.sellingPrice)}
                              </TableCell>
                              {showSummarySecondaryPsf && (
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                  {psfWithSuffix(u.psfDps ?? u.psf)}
                                </TableCell>
                              )}
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                {isPsfMetric
                                  ? psfWithSuffix(u.psfNps ?? u.psf)
                                  : moneyOrDash(u.sellingPriceNps ?? u.sellingPrice)}
                              </TableCell>
                              {showSummarySecondaryPsf && (
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                  {psfWithSuffix(u.psfNps ?? u.psf)}
                                </TableCell>
                              )}
                            </>
                          ) : isPsfMetric ? (
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              {psfWithSuffix(u.psf)}
                            </TableCell>
                          ) : (
                            <>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                {moneyOrDash(u.sellingPrice)}
                              </TableCell>
                              {showSummarySecondaryPsf && (
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                  {psfWithSuffix(u.psf)}
                                </TableCell>
                              )}
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}
        </Box>

        {/* Site map sidebar */}
        <Box
          sx={{
            flex: { md: '0 0 33%' },
            width: '100%',
            minWidth: 0,
            position: { md: 'sticky' },
            top: { md: 16 },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: 'var(--color-heading)', mb: 1 }}
          >
            Site map
          </Typography>
          {sitePlanImageUrl ? (
            <Box
              component="img"
              src={sitePlanImageUrl}
              alt="Site plan"
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: { md: 400 },
                objectFit: 'contain',
                borderRadius: 1,
                border: '1px solid rgba(var(--color-text-rgb), 0.1)',
              }}
            />
          ) : (
            <Box
              sx={{
                border: '1px dashed rgba(var(--color-text-rgb), 0.2)',
                borderRadius: 1,
                p: 3,
                textAlign: 'center',
                color: 'rgba(var(--color-text-rgb), 0.6)',
              }}
            >
              <Typography variant="caption">Site map not available.</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AvailableUnitsSection;
